import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import config from '@efdigital/ll-admin-v2/config/environment';
import { DataTypeLti } from '@efdigital/ll-admin-v2/data/data-types/lti';
import { LTI_API_PATH } from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consuming-platform-handler.ts';
import type { LtiResourceLink } from '@efdigital/ll-admin-v2/data/types/lti';
import { withReactiveResponse } from '@warp-drive/core/request';
import {
  createRecord,
  deleteRecord,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { LtiOperation } from './ops.ts';

const apis: Apis = config['ll-addon-auth-apis'];

function resourceLinksBaseUrl(
  platformId: string,
  consumedToolId: string,
  deploymentId: string
): string {
  return `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}/tools/${encodeURIComponent(consumedToolId)}/deployments/${encodeURIComponent(deploymentId)}/resource-links`;
}

function resourceLinkRequestBody(link: LtiResourceLink) {
  return {
    platformResourceLinkId: link.platformResourceLinkId,
    toolItemType: link.toolItemType,
    toolItemId: link.toolItemId,
    isActive: link.isActive,
  };
}

export function buildListResourceLinks(
  platformId: string,
  consumedToolId: string,
  deploymentId: string
) {
  const url = resourceLinksBaseUrl(platformId, consumedToolId, deploymentId);

  return withReactiveResponse<LtiResourceLink[]>({
    url,
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    op: LtiOperation.ListResourceLinks,
    cacheOptions: {
      key: url,
      types: [DataTypeLti.ResourceLink],
    },
  });
}

export function buildListResourceLinksByCohort(
  cohortId: string,
  clientId: string
) {
  const params = new URLSearchParams({
    toolItemType: 'cohorts',
    toolItemId: cohortId,
    clientId,
  });
  const url = `${apis.systemsApi.host}${LTI_API_PATH}/resource-links?${params.toString()}`;

  return withReactiveResponse<LtiResourceLink[]>({
    url,
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    op: LtiOperation.ListResourceLinksByCohort,
    cacheOptions: {
      key: url,
      types: [DataTypeLti.ResourceLink],
    },
  });
}

export function buildCreateResourceLink(
  link: LtiResourceLink,
  platformId: string,
  consumedToolId: string,
  deploymentId: string
) {
  const init = createRecord(link, {});

  init.url = resourceLinksBaseUrl(platformId, consumedToolId, deploymentId);
  init.body = JSON.stringify(resourceLinkRequestBody(link));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildUpdateResourceLink(
  link: LtiResourceLink,
  platformId: string,
  consumedToolId: string,
  deploymentId: string
) {
  const resourceLinkId = link.id;
  if (!resourceLinkId) {
    throw new Error('Cannot update a resource link without an id');
  }

  const init = updateRecord(link, {});

  init.url = `${resourceLinksBaseUrl(platformId, consumedToolId, deploymentId)}/${encodeURIComponent(resourceLinkId)}`;
  init.body = JSON.stringify(resourceLinkRequestBody(link));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildDeleteResourceLink(
  link: LtiResourceLink,
  platformId: string,
  consumedToolId: string,
  deploymentId: string
) {
  const resourceLinkId = link.id;
  if (!resourceLinkId) {
    throw new Error('Cannot delete a resource link without an id');
  }

  const init = deleteRecord(link, {});

  init.url = `${resourceLinksBaseUrl(platformId, consumedToolId, deploymentId)}/${encodeURIComponent(resourceLinkId)}`;
  init.headers.set('Content-Type', 'application/json');

  return init;
}
